import CopyBtn from "@/components/copy-btn";
import useFormattedDate from "@/dates/use-formatted-date";
import { cn } from "@/utils/cn";
import {
  ChevronRight,
  ExternalLink,
  HomeIcon,
  MessageCircleIcon,
} from "lucide-react";
import ReturnStatusBadge from "../../../components/return-status-badge";
import NavActions from "../return-nav/nav-actions";
import ReturnDetailsHeaderBlock from "./return-details-header-block";
import ReturnDetailsHeaderLabel from "./return-details-header-label";
import { useCurrentReturnRequest } from "../../../utils/current-return-request-context";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useTranslation from "@babackapp/translations/use-translation";
import { Link } from "react-router-dom";
import { useMemo } from "react";
import ReturnHasHilightedReasonProps from "../../../components/return-has-highlighted-reason";

type ReturnDetailsHeaderProps = {
  unreadCount: number;
  scrollToActivity: () => void;
};

export default function ReturnDetailsHeader({
  unreadCount,
  scrollToActivity,
}: ReturnDetailsHeaderProps) {
  const { data: shop } = useCurrentShopSuspense();
  const returnRequest = useCurrentReturnRequest();
  const t = useTranslation();
  const { orderedAt, createdAt } = returnRequest;
  const { humanDate: humanOrderedAt } = useFormattedDate(orderedAt);
  const { humanDate: humanCreatedAt } = useFormattedDate(createdAt);

  const commentsCount = useMemo(() => {
    return returnRequest.logs.filter((log) => log.type === "comment").length;
  }, [returnRequest.logs]);

  const hasUnread = unreadCount > 0;

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-2">
        <span className="flex items-center gap-2 text-muted-foreground text-sm">
          <Link to="/">
            <HomeIcon className="size-4" />
          </Link>{" "}
          <ChevronRight className="size-4" />{" "}
          <Link to={`/shops/${shop.id}/returns`} className="hover:underline">
            {t("sidebar.returns")}
          </Link>
          <ChevronRight className="size-4" /> {returnRequest.orderName}
        </span>
        <div className="flex items-center gap-4 justify-between">
          <span className="text-2xl font-bold">
            <CopyBtn value={returnRequest.orderName}>
              {returnRequest.orderName}
            </CopyBtn>
          </span>
          <div className="flex items-center gap-2">
            <ReturnHasHilightedReasonProps
              returnRequest={returnRequest}
              shop={shop}
            />
            {commentsCount > 0 && (
              <button
                type="button"
                onClick={scrollToActivity}
                aria-label={t("returnDetail.activity")}
                className={cn(
                  "flex items-center gap-2 transition-colors cursor-pointer",
                  hasUnread
                    ? "text-primary hover:text-primary/80"
                    : "hover:text-primary",
                )}
              >
                <div className="relative">
                  <MessageCircleIcon className="size-4" aria-hidden="true" />
                  {hasUnread && (
                    <span className="absolute -top-0.5 -right-0.5 flex size-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                      <span className="relative inline-flex rounded-full size-2 bg-primary" />
                    </span>
                  )}
                </div>
                <span
                  aria-hidden="true"
                  className={cn(
                    "text-sm",
                    hasUnread ? "font-semibold" : "font-medium",
                  )}
                >
                  {commentsCount}
                </span>
              </button>
            )}
            <ReturnStatusBadge statuses={returnRequest.status} />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-8 max-w-[80%]">
        <ReturnDetailsHeaderBlock>
          <ReturnDetailsHeaderLabel>
            {t("returnRequest.shipping_info.dateOfPurchase")}
          </ReturnDetailsHeaderLabel>
          <span className="text-sm font-medium">{humanOrderedAt}</span>
        </ReturnDetailsHeaderBlock>

        <ReturnDetailsHeaderBlock>
          <ReturnDetailsHeaderLabel>
            {t("returnRequest.shipping_info.returnDate")}
          </ReturnDetailsHeaderLabel>
          <span className="text-sm font-medium">{humanCreatedAt}</span>
        </ReturnDetailsHeaderBlock>

        <ReturnDetailsHeaderBlock>
          <ReturnDetailsHeaderLabel>
            {t("returnRequest.shipping_info.orderId")}
          </ReturnDetailsHeaderLabel>
          <a
            target="_blank"
            rel="noreferrer"
            href={`https://${shop.shopifyUrl}/admin/orders/${returnRequest.orderId}`}
            className="text-sm truncate max-w-[200px] inline-flex items-center underline font-medium gap-2"
          >
            {returnRequest.orderId} <ExternalLink className="size-4" />
          </a>
        </ReturnDetailsHeaderBlock>

        <ReturnDetailsHeaderBlock>
          <ReturnDetailsHeaderLabel>
            {t("returnRequest.shipping_info.logisticCode")}
          </ReturnDetailsHeaderLabel>
          <CopyBtn value={returnRequest.shipping_info?.logisticCode ?? ""}>
            <span className="text-sm font-medium truncate max-w-[200px]">
              {returnRequest.shipping_info?.logisticCode ?? "-"}
            </span>
          </CopyBtn>
        </ReturnDetailsHeaderBlock>
      </div>

      <NavActions />
    </div>
  );
}
